import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { attemptOpenDesktopApp } from '@/lib/utils/app-links'
import {
  hasHandledDesktopHandoff,
  isDesktopAppKnownInstalled,
  isDesktopHandoffLandingPage,
  isDesktopHandoffPage,
  markDesktopHandoffHandled,
} from '@/lib/utils/desktop-app-handoff'
import { DesktopIcon, XIcon } from '@phosphor-icons/react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'

/**
 * OpenInDesktopPrompt - shown only in the browser (never inside Tauri).
 *
 * On the initial, non-Today organization page it offers a manual fallback for
 * users whose browser has previously confirmed or opted into the desktop app.
 * It shares its handled marker with DeepLinkInterceptor so both surfaces can
 * never launch for the same visit, tab, or later browser session.
 */
export function OpenInDesktopPrompt() {
  const location = useLocation()
  const [visible, setVisible] = useState(false)
  const [isOpening, setIsOpening] = useState(false)
  const hasRun = useRef(false)
  const initialPath = useRef(location.pathname + location.search)

  const currentPath = initialPath.current

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    if (!isDesktopAppKnownInstalled()) return
    if (hasHandledDesktopHandoff()) return

    if (isDesktopHandoffLandingPage(currentPath)) {
      markDesktopHandoffHandled()
      return
    }

    if (!isDesktopHandoffPage(currentPath)) return

    markDesktopHandoffHandled()
    setVisible(true)
  }, [currentPath])

  if (!visible) return null

  const handleOpen = async () => {
    setIsOpening(true)
    const didOpen = await attemptOpenDesktopApp(currentPath)
    setIsOpening(false)
    if (didOpen) {
      setVisible(false)
    } else {
      toast.error(
        "Couldn't open the Scrambl desktop app. Make sure it's installed.",
      )
    }
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border bg-background p-4 shadow-lg">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-primary/10">
          <DesktopIcon className="h-5 w-5 text-primary" weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            {isOpening
              ? 'Opening in the desktop app…'
              : 'Open in the desktop app?'}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Scrambl works best in the desktop app. This page can open there
            automatically.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setVisible(false)}
          className="-mr-1 -mt-1 h-6 w-6 flex-shrink-0 text-muted-foreground"
          title="Dismiss"
        >
          <XIcon className="h-3.5 w-3.5" />
          <span className="sr-only">Dismiss</span>
        </Button>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => void handleOpen()}
          disabled={isOpening}
          className="h-7 text-xs"
        >
          {isOpening ? 'Opening…' : 'Open app'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setVisible(false)}
          className="h-7 text-xs text-muted-foreground"
        >
          Continue here
        </Button>
      </div>
    </div>
  )
}
